<template>
    <div>
        <app-layout>
            <template v-slot:header>
                <Header :title="$t('coop_posting_applications')"
                        :isColumns="true"
                        @filter-div-status="datatable.filterStatus = $event">
                    <template v-slot:columns>
                        <div class="mb-1" v-for="(column,key) in datatable.columns">
                            <b-form-checkbox
                                :id="'checkbox-'+key"
                                :name="'checkbox-'+key"
                                :value="false"
                                :unchecked-value="true"
                                v-model="column.hidden"
                                v-if="column.field!='buttons'"
                            >
                                {{ column.label }}
                            </b-form-checkbox>
                        </div>
                    </template>
                </Header>
            </template>
            <template v-slot:header-mobile>
                <HeaderMobile :title="$t('coop_posting_applications')"
                              :isColumns="true"
                              @filter-div-status="datatable.filterStatus = $event" />
            </template>

            <b-button variant="outline-secondary" class="mb-3" @click="$router.push('/coop/ads')">
                <i class="ri-arrow-left-line"></i> {{ $t('back') }}
            </b-button>

            <div class="mb-3" v-if="posting">
                <div><strong>{{ $t('coop_company') }}:</strong> {{ posting.company_name }}</div>
                <div><strong>{{ $t('coop_position') }}:</strong> {{ posting.position }}</div>
            </div>

            <datatable-filter v-show="datatable.filterStatus"
                              @filter="filter"
                              @filterClear="filterClear"
                              :export-excel="true"
                              @exportExcel="$refs.datatable.excel()">
                <b-row>
                    <b-col sm="12">
                        <fieldset class="border p-2 mb-2 rounded">
                            <legend class="w-auto px-2 small font-weight-bold">{{ $t('coop_gpa_info') }}</legend>
                            <b-row>
                                <b-col sm="6" md="3">
                                    <b-form-group :label="$t('coop_min')">
                                        <b-form-input v-model="datatable.queryParams.filter.gno_min" type="number" step="0.01"/>
                                    </b-form-group>
                                </b-col>
                                <b-col sm="6" md="3">
                                    <b-form-group :label="$t('coop_max')">
                                        <b-form-input v-model="datatable.queryParams.filter.gno_max" type="number" step="0.01"/>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                        </fieldset>
                    </b-col>
                    <b-col sm="6" md="3">
                        <b-form-group :label="$t('faculty')">
                            <faculty-selectbox v-model="datatable.queryParams.filter.faculty_code" />
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="3">
                        <b-form-group :label="$t('department')">
                            <department-selectbox :faculty_code="datatable.queryParams.filter.faculty_code"
                                                   v-model="datatable.queryParams.filter.department_code" />
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="3">
                        <b-form-group :label="$t('student_number')">
                            <b-form-input v-model="datatable.queryParams.filter.student_number"/>
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="3">
                        <b-form-group :label="$t('class')">
                            <classes-selectbox v-model="datatable.queryParams.filter.class" />
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="3">
                        <b-form-group :label="$t('status')">
                            <b-form-select v-model="datatable.queryParams.filter.status" :options="statusFilterOptions" />
                        </b-form-group>
                    </b-col>
                </b-row>
            </datatable-filter>

            <datatable ref="datatable"
                       :isLoading.sync="datatable.isLoading"
                       :columns="datatable.columns"
                       :rows="datatable.rows"
                       :total="datatable.total"
                       :queryParams="datatable.queryParams"
                       :lineNumbers="false"
                       :select-options="true"
                       @on-page-change="onPageChange"
                       @on-sort-change="onSortChange"
                       @on-per-page-change="onPerPageChange"
                       @on-selected-rows-change="onSelectedRowsChange"
                       v-show="datatable.showTable"
            />

            <div class="d-flex mt-2" v-if="datatable.showTable">
                <b-button variant="outline-primary" @click="downloadSelectedCvs">
                    {{ $t('coop_download_selected_cvs') }}
                </b-button>
            </div>

            <CommonModal ref="statusModal" size="md" :onHideOnlyX="true">
                <template v-slot:CommonModalTitle>
                    {{ $t('status_update') }}
                </template>
                <template v-slot:CommonModalContent>
                    <div v-if="selectedApplication">
                        <b-form-group :label="$t('status')">
                            <parameter-selectbox v-model="statusForm.status" code="coop_internship_application.statuses" />
                        </b-form-group>

                        <b-form-group :label="$t('description')" v-if="statusForm.status === 'rejected'">
                            <b-form-textarea v-model="statusForm.reject_reason" rows="3"/>
                        </b-form-group>

                        <div v-if="statusForm.status === 'company_evaluation'">
                            <b-row>
                                <b-col cols="6">
                                    <b-form-group :label="$t('interview_date')">
                                        <SelectDate v-model="statusForm.interview_date"/>
                                    </b-form-group>
                                </b-col>
                                <b-col cols="6">
                                    <b-form-group :label="$t('interview_time')">
                                        <b-form-input v-model="statusForm.interview_time" type="time"/>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                        </div>

                        <div v-if="statusForm.status === 'waiting_student_documents'">
                            <b-form-group :label="$t('coop_documents')">
                                <multi-parameter-selectbox code="coop_application.documents" v-model="statusForm.documents" />
                            </b-form-group>
                        </div>

                        <div v-if="statusForm.status === 'approved'">
                            <b-row>
                                <b-col cols="6">
                                    <b-form-group :label="$t('work_start_date')">
                                        <SelectDate v-model="statusForm.job_start_date"/>
                                    </b-form-group>
                                </b-col>
                                <b-col cols="6">
                                    <b-form-group :label="$t('work_end_date')">
                                        <SelectDate v-model="statusForm.job_end_date"/>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            <div class="small text-muted mb-2">{{ $t('coop_approval_email_note') }}</div>
                            <div class="small text-muted mb-2">{{ $t('coop_survey_note') }}</div>
                        </div>

                        <div class="d-flex justify-content-center mt-2">
                            <b-button variant="primary" @click="saveStatus">{{ $t('save') }}</b-button>
                        </div>
                    </div>
                </template>
            </CommonModal>

            <CommonModal ref="documentsModal" size="lg" :onHideOnlyX="true">
                <template v-slot:CommonModalTitle>
                    {{ $t('coop_documents') }}
                </template>
                <template v-slot:CommonModalContent>
                    <div v-if="selectedApplication">
                        <div v-if="documentsForm && documentsForm.length > 0">
                            <div v-for="(doc, idx) in documentsForm" :key="idx" class="card mb-3 shadow-sm border bg-light">
                                <div class="card-body p-3">
                                    <div class="d-flex align-items-center justify-content-between flex-wrap">
                                        <div class="d-flex align-items-center mb-2 mb-md-0">
                                            <i class="ri-file-text-line text-primary mr-2" style="font-size: 1.25rem;"></i>
                                            <span class="font-weight-bold">{{ doc.type || doc.label || doc.code }}</span>
                                        </div>
                                        <div class="d-flex align-items-center flex-wrap">
                                            <b-button size="sm" variant="outline-secondary" @click="downloadDocument(doc)" class="mr-2">
                                                <i class="ri-download-2-line mr-1"></i> {{ $t('download') || 'İndir' }}
                                            </b-button>

                                            <b-form-select size="sm" v-model="doc.status" :options="documentStatusOptions" style="width: 150px;" class="mr-2" />

                                            <b-form-input size="sm" v-if="doc.status === 'rejected'" v-model="doc.reason" :placeholder="$t('reason')" style="width: 180px;" class="mr-2" />

                                            <b-button size="sm" variant="primary" @click="saveSingleDocumentStatus(doc)">
                                                <i class="ri-save-line mr-1"></i> {{ $t('save') }}
                                            </b-button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else class="text-center p-4 text-muted border rounded">
                            <i class="ri-inbox-line ri-2x mb-2 d-block"></i>
                            {{ $t('no_result') }}
                        </div>
                    </div>
                </template>
            </CommonModal>
        </app-layout>
    </div>
</template>
<script>
    //Template
    import AppLayout from '@/layouts/AppLayout';
    import Header from '@/layouts/AppLayout/Header';
    import HeaderMobile from '@/layouts/AppLayout/HeaderMobile';

    //Component
    import DatatableFilter from '@/components/datatable/DatatableFilter';
    import Datatable from '@/components/datatable/Datatable';
    import CommonModal from '@/components/elements/CommonModal';
    import FacultySelectbox from '@/components/interactive-fields/FacultySelectbox.vue';
    import DepartmentSelectbox from '@/components/interactive-fields/DepartmentSelectbox';
    import ClassesSelectbox from '@/components/interactive-fields/ClassesSelectbox';
    import ParameterSelectbox from '@/components/interactive-fields/ParameterSelectbox.vue';
    import MultiParameterSelectbox from '@/components/interactive-fields/MultiParameterSelectbox.vue';
    import SelectDate from '@/components/interactive-fields/SelectDate.vue';

    //Service
    import CoopAdService from '@/services/CoopAdService';
    import CoopApplicationService from '@/services/CoopApplicationService';

    //Other
    import qs from 'qs';

    export default {
        components: {
            AppLayout,
            Header,
            HeaderMobile,

            DatatableFilter,
            Datatable,
            CommonModal,
            FacultySelectbox,
            DepartmentSelectbox,
            ClassesSelectbox,
            ParameterSelectbox,
            MultiParameterSelectbox,
            SelectDate
        },
        data() {
            return {
                posting: null,
                selectedApplication: null,
                selectedRows: [],
                statusForm: {
                    status: null,
                    reject_reason: null,
                    job_start_date: null,
                    job_end_date: null,
                    interview_date: null,
                    interview_time: null,
                    documents: []
                },
                documentsForm: [],
                statusFilterOptions: [
                    {value: null, text: ''},
                    {value: 'reviewing', text: this.$t('coop_status_reviewing')},
                    {value: 'sent_to_company', text: this.$t('coop_status_sent_to_company')},
                    {value: 'company_evaluation', text: this.$t('coop_status_company_evaluation')},
                    {value: 'waiting_student_documents', text: this.$t('coop_status_waiting_student_documents')},
                    {value: 'document_review', text: this.$t('coop_status_document_review')},
                    {value: 'approved', text: this.$t('approved')},
                    {value: 'rejected', text: this.$t('rejected')},
                    {value: 'position_closed', text: this.$t('coop_status_position_closed')}
                ],
                documentStatusOptions: [
                    {value: 'approved', text: this.$t('approved')},
                    {value: 'rejected', text: this.$t('rejected')}
                ],
                datatable: {
                    isLoading: false,
                    columns: [
                        {
                            label: '',
                            field: 'buttons',
                            html: true,
                            hidden: false,
                            tdClass: 'p-0 text-center w-40 align-middle',
                            buttons: [
                                {
                                    text: this.$t('status_update'),
                                    class: 'ri-refresh-line',
                                    callback: (row) => {
                                        this.showStatusModal(row);
                                    }
                                },
                                {
                                    text: this.$t('coop_delete_application'),
                                    class: 'ri-delete-bin-2-line',
                                    callback: (row) => {
                                        this.deleteApplication(row);
                                    }
                                },
                                {
                                    text: this.$t('coop_update_document_status'),
                                    class: 'ri-file-list-3-line',
                                    callback: (row) => {
                                        this.showDocumentsModal(row);
                                    }
                                }
                            ]
                        },
                        {
                            label: this.toUpperCase('student_number'),
                            field: 'student_number',
                            formatFn: (val, row) => val ?? row.studentProgram?.student?.student_number ?? '',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('name'),
                            field: 'name',
                            formatFn: (val, row) => val ?? row.studentProgram?.student?.name ?? '',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('surname'),
                            field: 'surname',
                            formatFn: (val, row) => val ?? row.studentProgram?.student?.surname ?? '',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('department'),
                            field: 'department_name',
                            formatFn: (val, row) => val ?? row.studentProgram?.program?.department_name ?? '',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('gno'),
                            field: 'gno',
                            formatFn: (val, row) => val ?? row.min_gpa ?? row.gpa ?? '',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('status'),
                            field: 'status',
                            formatFn: (status) => {
                                const found = this.statusFilterOptions.find((option) => option.value === status);
                                return found ? found.text : status;
                            },
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('class'),
                            field: 'class',
                            formatFn: (val, row) => val ?? row.studentProgram?.class ?? '',
                            hidden: false
                        }
                    ],
                    rows: [],
                    total: 0,
                    filterStatus: true,
                    showTable: false,
                    queryParams: {
                        filter: {},
                        sort: 'id',
                        page: 1,
                        limit: 20
                    }
                }
            };
        },
        created() {
            CoopAdService.get(this.$route.params.id).then((response) => {
                this.posting = response.data.data || response.data;
            });
            this.getRows();
        },
        methods: {
            filter() {
                this.datatable.queryParams.page = 1;
                this.getRows();
            },
            filterClear() {
                this.datatable.queryParams.filter = {};
                this.getRows();
            },
            onPageChange(params) {
                this.datatable.queryParams.page = params.currentPage;
                this.getRows();
            },
            onPerPageChange(params) {
                this.datatable.queryParams.limit = params.currentPerPage;
                this.datatable.queryParams.page = 1;
                this.getRows();
            },
            onSortChange(params) {
                const sortType = params[0].type == 'desc' ? '-' : '';
                this.datatable.queryParams.sort = sortType + params[0].field;
                this.getRows();
            },
            onSelectedRowsChange(params) {
                this.selectedRows = params.selectedRows;
            },
            getRows() {
                this.datatable.showTable = true;
                this.datatable.isLoading = true;

                const config = {
                    params: {
                        ...this.datatable.queryParams,
                        filter: {
                            ...this.datatable.queryParams.filter,
                            posting_id: this.$route.params.id
                        }
                    },
                    paramsSerializer: (params) => qs.stringify(params, {encode: false})
                };

                return CoopApplicationService.getAll(config)
                    .then((response) => {
                        this.datatable.rows = response.data.data;
                        this.datatable.total = response.data.pagination?.total || response.data.data.length;
                    })
                    .finally(() => {
                        this.datatable.isLoading = false;
                    });
            },
            showStatusModal(row) {
                this.selectedApplication = row;
                this.statusForm = {
                    status: row.status,
                    reject_reason: row.reject_reason || null,
                    job_start_date: row.job_start_date || null,
                    job_end_date: row.job_end_date || null,
                    interview_date: row.interview_date || null,
                    interview_time: row.interview_time || null,
                    documents: row.documents ? row.documents.map(d => typeof d === 'string' ? d : d.type) : []
                };
                this.$refs.statusModal.$refs.commonModal.show();
            },
            saveStatus() {
                const payload = {
                    status: this.statusForm.status,
                    reject_reason: this.statusForm.status === 'rejected' ? this.statusForm.reject_reason : null,
                    job_start_date: this.statusForm.status === 'approved' ? this.statusForm.job_start_date : null,
                    job_end_date: this.statusForm.status === 'approved' ? this.statusForm.job_end_date : null,
                    interview_date: this.statusForm.status === 'company_evaluation' ? this.statusForm.interview_date : null,
                    interview_time: this.statusForm.status === 'company_evaluation' ? this.statusForm.interview_time : null,
                    documents: this.statusForm.status === 'waiting_student_documents' ? this.statusForm.documents : null
                };

                CoopApplicationService.updateStatus(this.selectedApplication.id, payload)
                    .then((response) => {
                        this.$toast.success(this.$t('api.' + response.data.message));
                        this.$refs.statusModal.$refs.commonModal.hide();
                        this.getRows();
                    })
                    .catch((error) => {
                        this.showErrors(error, null);
                    });
            },
            deleteApplication(row) {
                this.$swal.fire({
                    text: this.$t('are_you_sure_to_delete'),
                    showCancelButton: true,
                    confirmButtonText: this.$t('yes'),
                    cancelButtonText: this.$t('no')
                }).then((result) => {
                    if (result.isConfirmed) {
                        CoopApplicationService.remove(row.id)
                            .then((response) => {
                                this.$toast.success(this.$t('api.' + response.data.message));
                                this.getRows();
                            })
                            .catch((error) => {
                                this.showErrors(error, null);
                            });
                    }
                });
            },
            showDocumentsModal(row) {
                this.selectedApplication = row;
                const docList = row.documents || row.required_documents || [];
                this.documentsForm = docList.map((doc) => {
                    if (typeof doc === 'string') {
                        return { type: doc, status: 'approved', reason: null };
                    }
                    return { ...doc, status: doc.status || 'approved', reason: doc.reason || null };
                });
                this.$refs.documentsModal.$refs.commonModal.show();
            },
            downloadDocument(doc) {
                const docType = doc.type || doc.code;
                CoopApplicationService.showDocument(this.selectedApplication.id, { type: docType })
                    .then((response) => {
                        this._downloadFile(response, `${docType}.pdf`);
                    })
                    .catch(() => {});
            },
            saveSingleDocumentStatus(doc) {
                const payload = {
                    type: doc.type || doc.code,
                    status: doc.status,
                    reason: doc.status === 'rejected' ? doc.reason : null
                };

                CoopApplicationService.updateDocumentStatus(this.selectedApplication.id, payload)
                    .then((response) => {
                        this.$toast.success(this.$t('api.' + response.data.message));
                        this.getRows();
                    })
                    .catch((error) => {
                        this.showErrors(error, null);
                    });
            },
            downloadSelectedCvs() {
                if (!this.selectedRows || this.selectedRows.length === 0) {
                    return;
                }
                this.selectedRows.forEach(row => {
                    const docList = row.documents || [];
                    if (docList.length === 0 && row.cv_type) {
                        CoopApplicationService.showDocument(row.id, { type: row.cv_type })
                            .then(res => this._downloadFile(res, `CV_${row.student_number || row.id}.pdf`))
                            .catch(() => {});
                    } else {
                        docList.forEach(doc => {
                            const docType = typeof doc === 'string' ? doc : (doc.type || doc.code);
                            if (docType) {
                                CoopApplicationService.showDocument(row.id, { type: docType })
                                    .then(res => this._downloadFile(res, `${docType}_${row.student_number || row.id}.pdf`))
                                    .catch(() => {});
                            }
                        });
                    }
                });
            }
        }
    }
</script>
